export interface Point {
  x: number;
  y: number;
}

export interface Pinch {
  distance: number;
  center: Point;
}

function whichTransitionEvent(): string | null {
  const transitions = {
    transition: 'transitionend',
    OTransition: 'oTransitionEnd',
    MozTransition: 'transitionend',
    WebkitTransition: 'webkitTransitionEnd'
  };

  const element = document.createElement('element');
  const transitionEvent = Object.entries(transitions).find(([api]) => element.style[api] !== undefined);

  return transitionEvent ? transitionEvent[1] : null;
}

function transformElement(element: Element | null, scale: number, translate: Point): Promise<void> {
  if (element === null) {
    return Promise.resolve();
  }

  (element as HTMLElement).style.transform = `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`;

  return new Promise((resolve) => {
    if (getComputedStyle(element).transitionDuration === '0s') {
      return resolve();
    }

    const transitionEvent = whichTransitionEvent();
    if (transitionEvent) {
      return element.addEventListener(transitionEvent, function transCallback() {
        element.removeEventListener(transitionEvent, transCallback);
        return resolve();
      });
    }
    return resolve();
  });
}

function getFocusPoint(event: Event): Point {
  const { pageX = 0, pageY = 0 } = event.type.includes('touch')
    ? (event as TouchEvent).touches[0] || (event as TouchEvent).changedTouches[0]
    : { pageX: (event as MouseEvent).pageX, pageY: (event as MouseEvent).pageY };
  return {
    x: pageX,
    y: pageY
  };
}

function getPinchProps({ touches }: TouchEvent): Pinch {
  return {
    distance: Math.sqrt(
      Math.abs(touches[1].clientX - touches[0].clientX) ** 2 + Math.abs(touches[1].clientY - touches[0].clientY) ** 2
    ),
    center: {
      x: (touches[0].pageX + touches[1].pageX) / 2,
      y: (touches[0].pageY + touches[1].pageY) / 2
    }
  };
}

function getNumberOfTouches(event: TouchEvent): number {
  return event.touches.length + event.changedTouches.length;
}

function getMovementShift(current: Point | undefined, previous: Point | undefined, slowdown = 2): Point {
  return current && previous
    ? {
        x: (current.x - previous.x) / slowdown,
        y: (current.y - previous.y) / slowdown
      }
    : { x: 0, y: 0 };
}

function getProductRoundelsList(roundels: string): string[] {
  return roundels.split(',').map((p) => p.replace(/[^A-Za-z0-9\s./-_]/g, '').trim());
}

function detectSamsungBrowser(): boolean {
  return !!navigator.userAgent.match(/SamsungBrowser/i);
}

export {
  transformElement,
  getNumberOfTouches,
  getFocusPoint,
  getMovementShift,
  getPinchProps,
  getProductRoundelsList,
  detectSamsungBrowser
};
